import { Button, Dialog, DialogActions, DialogContent } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { groupBy, keys, template } from 'lodash'
import { pgsseIndicatorValue } from 'pgsse/dto/PgsseShapes'
import { NewTable } from 'components/datatable/NewTable'
import { getYear } from 'utils/DateUtil'
import { getLabel } from 'utils/StoreUtils'
import { shallowEqual, useSelector } from 'react-redux'
import NumberField from 'components/forms/NumberField'
import moment from 'moment'
import { REDUCER_INDICATORS } from 'pgsse/constant/PgsseConstants'
import { useParams } from 'react-router'

const IndicatorsDialog = ({
    updatedData = [],
    selectedOperator,
    isOpen = false,
    setIsOpen = () => { },
    dispatchStateAUD = () => { },
}) => {
    const {
        indicators,
        contributors,
        accountUser,
    } = useSelector(store => ({
        indicators: store.PgsseReducer.indicators,
        contributors: store.ContributorReducer.contributors,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const [addedIndicators, setAddedIndicators] = useState([])
    const [updatedIndicators, setUpdatedIndicators] = useState([])

    const { idDiagnostic } = useParams()

    const months = useMemo(() => [
        { key: '01', value: i18n.januaryShort }, { key: '02', value: i18n.februaryShort }, { key: '03', value: i18n.marchShort },
        { key: '04', value: i18n.aprilShort }, { key: '05', value: i18n.mayShort }, { key: '06', value: i18n.juneShort },
        { key: '07', value: i18n.julyShort }, { key: '08', value: i18n.augustShort }, { key: '09', value: i18n.septemberShort },
        { key: '10', value: i18n.octoberShort }, { key: '11', value: i18n.novemberShort }, { key: '12', value: i18n.decemberShort },
    ], [])

    const headers = ['definition', 'year', ...months]

    const onValidate = () => {
        dispatchStateAUD({
            type: REDUCER_INDICATORS.ADD,
            payload: addedIndicators,
        })
        dispatchStateAUD({
            type: REDUCER_INDICATORS.UPDATE,
            payload: updatedIndicators,
        })
        setAddedIndicators([])
        setUpdatedIndicators([])
        setIsOpen(false)
    }

    const onChange = useCallback((idIndicator, month, value, year) => {
        const found = updatedData.find(ud => ud.idIndicator === idIndicator && ud.idOperator === selectedOperator && moment(ud.month).month() === month - 1)

        if (found) {
            setUpdatedIndicators(prev => [
                ...prev.filter(p => !(p.idIndicator === idIndicator && p.idOperator === selectedOperator && moment(p.month).month() === month - 1)),
                { ...found, value, updateDate: moment().valueOf(), updateLogin: accountUser.login },
            ])
        } else {
            setAddedIndicators(prev => [
                ...prev.filter(p => !(p.idIndicator === idIndicator && p.idOperator === selectedOperator && moment(p.month).month() === month - 1)),
                {
                    idDiagnostic: parseInt(idDiagnostic),
                    idIndicator,
                    month: moment().year(year).month(month - 1).startOf('month').valueOf(),
                    value,
                    updateLogin: accountUser.login,
                    updateDate: moment().valueOf(),
                    idOperator: selectedOperator,
                },
            ])
        }
    }, [accountUser.login, idDiagnostic, selectedOperator, updatedData])

    const tableData = useMemo(() => {
        const filteredData = [
            ...updatedData.filter(ud => ud.idOperator === selectedOperator),
            ...addedIndicators,
        ].map(di => updatedIndicators.find(ui => ui.month === di.month && ui.idIndicator === di.idIndicator && ui.idOperator === di.idOperator) || di)
        const grouped = groupBy(filteredData, d => `${getYear(d.month)}-${d.idOperator}-${d.idIndicator}`)
        return keys(grouped).map(key => {
            const values = grouped[key]
            const [year, idOperator, idIndicator] = key.split('-').map(d => parseInt(d))
            return {
                idOperator,
                idIndicator,
                year,
                definition: getLabel(indicators, idIndicator, 'name', 'idIndicator'),
                ...months.reduce((acc, month) => {
                    return {
                        ...acc,
                        [month.key]: <NumberField
                            key={`${idIndicator}-${month.key}`}
                            value={values.find(v => moment(v.month).month() === month.key - 1)?.value}
                            onChange={(v) => onChange(idIndicator, month.key, v, year)}
                        />,
                    }
                }, {}),
            }
        })
    }, [addedIndicators, indicators, months, onChange, selectedOperator, updatedData, updatedIndicators])

    return (
        <Dialog
            maxWidth='lg'
            open={isOpen}
        >
            <DefaultDialogTitle
                title={template(i18n.indicatorsEntry)({ contributors: getLabel(contributors, selectedOperator) })}
                onClose={() => {
                    setAddedIndicators([])
                    setUpdatedIndicators([])
                    setIsOpen(false)
                }}
            />
            <DialogContent>
                <NewTable
                    rows={tableData}
                    headers={headers}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    variant='contained'
                    onClick={onValidate}
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

IndicatorsDialog.propTypes = {
    updatedData: PropTypes.arrayOf(pgsseIndicatorValue),
    selectedOperator: PropTypes.number,
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    readMode: PropTypes.bool,
    dispatchStateAUD: PropTypes.func,
}

export default IndicatorsDialog
